import { Box, CircularProgress } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import {
  FormDataConsumer,
  FormTab,
  FormTabProps,
  SelectInput,
  SimpleFormIterator,
  useGetList,
  useInput,
} from 'react-admin';
import { OrganizationRecord, ScopeKey } from 'src/types';
import { CraftPageSection } from 'src/components/CraftPageSection';
import ArrayInput from 'src/inputs/ArrayInput';
import AutocompleteArrayInput from 'src/inputs/AutocompleteArrayInput';
import { useCompanySubscriptionSets } from 'src/resources/portal/organizations/hooks/useCompanySubscriptionSets';
import { usePassportScopes } from '../usePassportScopes';

const TITLE = 'Subscription Set Mappings';

/**
 * ReferenceArrayInput can't be used here because participants are referenced by
 * organization guid, but the organizations resource uses numeric id for lookups.
 */
const ParticipantInput = ({ source }: { source: string }) => {
  const { field } = useInput({ source });
  const selectedIds: string[] = field.value ?? [];
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    debounceRef.current = setTimeout(() => setDebouncedSearch(searchText), 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchText]);

  const { data: searchResults } = useGetList<OrganizationRecord>('organizations', {
    filter: {
      'deleted_at@_is_null': true,
      ...(debouncedSearch ? { 'name@_ilike': `%${debouncedSearch}%` } : {}),
    },
    sort: { field: 'name', order: 'ASC' },
    pagination: { page: 1, perPage: 25 },
  });

  const { data: selectedOrgs } = useGetList<OrganizationRecord>(
    'organizations',
    {
      filter: { 'guid@_in': selectedIds },
      pagination: { page: 1, perPage: Math.max(selectedIds.length, 1) },
    },
    { enabled: selectedIds.length > 0 },
  );

  const choiceMap = new Map<string, { id: string; name: string }>();
  for (const org of [...(selectedOrgs ?? []), ...(searchResults ?? [])]) {
    choiceMap.set(org.guid, { id: org.guid, name: org.name });
  }

  return (
    <AutocompleteArrayInput
      source={source}
      label="Participant Organizations"
      choices={Array.from(choiceMap.values())}
      matchSuggestion={() => true}
      onInputChange={(_: unknown, val: string, reason: string) => {
        if (reason === 'input' || reason === 'clear') setSearchText(val);
      }}
    />
  );
};

interface SubscriptionSetMappingsProps extends Omit<FormTabProps, 'label'> {
  companyDataApiKey?: string | undefined;
}

const SubscriptionSetMappings = ({ companyDataApiKey, ...rest }: SubscriptionSetMappingsProps) => {
  const { companySubscriptionSets: baseCompanySubscriptionSets, isLoading } =
    useCompanySubscriptionSets(companyDataApiKey);

  const subscriptionSetChoices = baseCompanySubscriptionSets.map((s) => ({ id: String(s.id), name: s.name }));
  const { scopeKeys } = usePassportScopes();

  return (
    <FormTab label={TITLE} {...rest}>
      <CraftPageSection title={TITLE}>
        {isLoading ? (
          <Box textAlign="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <ArrayInput source="subscriptionSetMappings" label={false}>
            <SimpleFormIterator fullWidth disableReordering>
              <FormDataConsumer>
                {({ formData, scopedFormData }) => {
                  const currentId = scopedFormData?.subscriptionSetId;
                  const usedIds = new Set(
                    (formData.subscriptionSetMappings ?? [])
                      .map((m: { subscriptionSetId: string }) => m.subscriptionSetId)
                      .filter((id: string) => id && id !== currentId),
                  );
                  const scopeLabels = formData.scopeLabels ?? {};
                  const scopeChoices = scopeKeys.map((key) => ({
                    id: key as ScopeKey,
                    name: scopeLabels[key]?.displayLabel || key,
                  }));
                  return (
                    <>
                      <SelectInput
                        source="subscriptionSetId"
                        choices={subscriptionSetChoices.filter((c) => !usedIds.has(c.id))}
                        label="Subscription Set"
                        fullWidth
                      />
                      <SelectInput source="scope" choices={scopeChoices} label="Scope" defaultValue="NONE" fullWidth />
                    </>
                  );
                }}
              </FormDataConsumer>
              <ParticipantInput source="participantOrgIds" />
            </SimpleFormIterator>
          </ArrayInput>
        )}
      </CraftPageSection>
    </FormTab>
  );
};

export default SubscriptionSetMappings;
